import { useCustomerTab } from '@/components/customers/useCustomerTab'
import { Field } from "@/components/common/Field";
export { Field } from "@/components/common/Field";
import {
  ActionWorkspace,
  actionStatus,
} from "@/components/actions/ActionWorkspace";
import { useState, type ReactNode, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  Row,
  Table,
  Tab,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBuilding,
  faListCheck,
  faPlus,
  faChartColumn,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import { useDemo, type Customer, type Action } from "@/context/DemoContext";
import { DashboardPage } from "./Dashboard";
import { CustomersPage } from "./Customers";
import { useAuth } from "@/context/AuthContext";
import { LiveActionsScreen, LiveAddActionScreen } from "./Actions";
import { LiveCustomerDetail } from "./CustomerDetail";
import { LiveCustomerEditor } from "./CustomerEditor";
export function Heading({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        <div className="eyebrow">WORKSPACE / CRM</div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      <div>{children}</div>
    </div>
  );
}
export function PreviewGate({ children }: { children: ReactNode }) {
  const { isDemo } = useAuth();
  return isDemo ? (
    <>{children}</>
  ) : (
    <Alert variant="info">
      These screens are available in demo mode while the CRM API is being
      prepared. Sign out and choose “Explore demo CRM” to review them.
    </Alert>
  );
}
export function CustomerTable({ rows }: { rows: Customer[] }) {
  return (
    <Table responsive hover className="mb-0">
      <thead>
        <tr>
          <th>Customer</th>
          <th>Account</th>
          <th>Category</th>
          <th>Location</th>
          <th>Account owner</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.id}>
            <td>
              <Link className="fw-semibold" to={`/customers/${c.id}`}>
                {c.name}
              </Link>
              <div className="small text-body-secondary">{c.type}</div>
            </td>
            <td>{c.account}</td>
            <td>{c.category}</td>
            <td>
              {c.city}
              <div className="small text-body-secondary">{c.postcode}</div>
            </td>
            <td>{c.owner}</td>
            <td>
              <Badge bg={c.onStop ? "warning" : "success"}>
                {c.onStop ? "On stop" : "Active"}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
export function DashboardScreen() {
  const { customers, actions } = useDemo();
  const { isDemo } = useAuth();
  if (!isDemo) return <DashboardPage />;
  return (
    <PreviewGate>
      <Heading
        title="Your CRM workspace"
        subtitle="Keep customers close and your next actions in view."
      >
        <Link className="btn btn-primary" to="/customers/new">
          <FontAwesomeIcon icon={faPlus} /> Add customer
        </Link>
      </Heading>
      <Row className="g-3 mb-4">
        {[
          ["Customers", customers.length],
          ["Open actions", actions.filter((a) => !a.closed).length],
          [
            "Overdue",
            actions.filter((a) => !a.closed && a.due < "2026-10-08").length,
          ],
          [
            "Trading accounts",
            customers.filter((c) => c.category === "Trading").length,
          ],
        ].map(([label, value]) => (
          <Col sm={6} xl={3} key={label}>
            <Card className="metric">
              <Card.Body>
                <span>{label}</span>
                <strong>{value}</strong>
                <small>Sample workspace</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <Row className="g-3 mb-4">
        {[
          [
            "My actions",
            "Plan your calls, visits and follow-ups.",
            "/actions/mine",
            faListCheck,
          ],
          [
            "All actions",
            "Every open action for this business.",
            "/actions/team",
            faListCheck,
          ],
          [
            "Customers",
            "Search accounts and manage relationships.",
            "/customers",
            faBuilding,
          ],
          [
            "Reports",
            "Explore your customers and team activity.",
            "/reports",
            faChartColumn,
          ],
          [
            "Configuration",
            "Manage users and reference data.",
            "/admin",
            faGear,
          ],
        ].map(([title, desc, url, icon]) => (
          <Col md={6} xl={3} key={String(title)}>
            <Link className="shortcut" to={String(url)}>
              <FontAwesomeIcon icon={icon as typeof faGear} />
              <h3>{String(title)}</h3>
              <p>{String(desc)}</p>
              <span>
                Open <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </Link>
          </Col>
        ))}
      </Row>
      <Card>
        <Card.Header>Customer overview</Card.Header>
        <CustomerTable rows={customers} />
      </Card>
    </PreviewGate>
  );
}
export function CustomersScreen() {
  const { customers } = useDemo();
  const [q, setQ] = useState("");
  const [owner, setOwner] = useState("All owners");
  const { isDemo } = useAuth();
  const rows = customers.filter(
    (c) =>
      `${c.name} ${c.postcode} ${c.account}`
        .toLowerCase()
        .includes(q.toLowerCase()) &&
      (owner === "All owners" || c.owner === owner),
  );
  if (!isDemo) return <CustomersPage />;
  return (
    <PreviewGate>
      <Heading
        title="Customers"
        subtitle="Find an account, manage contacts and keep the conversation moving."
      >
        <Link className="btn btn-primary" to="/customers/new">
          + Add customer
        </Link>
      </Heading>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={8}>
              <Field
                label="Search customers"
                value={q}
                onChange={setQ}
                placeholder="Name, postcode or account number"
              />
            </Col>
            <Col md={4}>
              <Field
                label="Account owner"
                value={owner}
                onChange={setOwner}
                options={["All owners", "Alex Morgan", "Jamie Taylor"]}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Header>{rows.length} customers</Card.Header>
        {rows.length ? (
          <CustomerTable rows={rows} />
        ) : (
          <p className="p-4 mb-0">No customers match your search.</p>
        )}
      </Card>
    </PreviewGate>
  );
}
const empty: Customer = {
  id: "",
  name: "",
  account: "",
  category: "Prospect",
  type: "Public house",
  address: "",
  city: "",
  postcode: "",
  phone: "",
  email: "",
  owner: "Alex Morgan",
  grid: "A1",
  discount: "0%",
  callDay: "Monday",
  deliveryDay: "Wednesday",
  onStop: false,
  notes: "",
};
export function CustomerEditor() {
  const { isDemo } = useAuth();
  if (!isDemo) return <LiveCustomerEditor />;
  return <DemoCustomerEditor />;
}
function DemoCustomerEditor() {
  const { id } = useParams();
  const { customers, setCustomers } = useDemo();
  const existing = customers.find((c) => c.id === id);
  const [c, setC] = useState<Customer>(existing ?? empty);
  const navigate = useNavigate();
  if (id && !existing)
    return <Alert variant="warning">Customer not found.</Alert>;
  function field(
    key: keyof Customer,
    label: string,
    options?: string[],
    type?: string,
    required = false,
  ) {
    return (
      <Field
        label={label}
        value={String(c[key])}
        onChange={(v) => setC({ ...c, [key]: v })}
        options={options}
        type={type}
        required={required}
      />
    );
  }
  return (
    <PreviewGate>
      <Heading
        title={id ? "Edit customer" : "New customer"}
        subtitle="Account, contact and order preferences."
      />
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          const next = { ...c, id: c.id || crypto.randomUUID() };
          setCustomers(
            id
              ? customers.map((x) => (x.id === id ? next : x))
              : [...customers, next],
          );
          navigate(`/customers/${next.id}`);
        }}
      >
        <Row className="g-4">
          <Col lg={7}>
            <Card>
              <Card.Header>Customer & contact details</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    {field("name", "Customer name", undefined, undefined, true)}
                    {field("account", "Account number")}
                    {field("category", "Category", [
                      "Prospect",
                      "Trading",
                      "Inactive",
                    ])}
                    {field("address", "Address")}
                    {field("city", "City")}
                    {field("postcode", "Postcode", undefined, undefined, true)}
                  </Col>
                  <Col md={6}>
                    {field("phone", "Telephone")}
                    {field("email", "Email", undefined, "email")}
                    {field("owner", "Account owner", [
                      "Alex Morgan",
                      "Jamie Taylor",
                    ])}
                    {field("notes", "General notes", undefined, "textarea")}
                    <Form.Check
                      id="account-on-stop"
                      label="Account on stop"
                      checked={c.onStop}
                      onChange={(e) => setC({ ...c, onStop: e.target.checked })}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5}>
            <Card>
              <Card.Header>Order preferences</Card.Header>
              <Card.Body>
                {field("type", "Customer type", [
                  "Public house",
                  "Hotel",
                  "Restaurant",
                ])}
                {field("discount", "Default discount", ["0%", "5%", "10%"])}
                {field("grid", "Grid reference", ["A1", "A2", "B1", "B2"])}
                {field("callDay", "Call day", [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                ])}
                {field("deliveryDay", "Delivery day", [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                ])}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <div className="mt-4 d-flex gap-2">
          <Button type="submit">Save customer</Button>
          <Link
            className="btn btn-outline-secondary"
            to={id ? `/customers/${id}` : "/customers"}
          >
            Cancel
          </Link>
        </div>
      </Form>
    </PreviewGate>
  );
}
export function CustomerDetail() {
  const { isDemo } = useAuth();
  if (!isDemo) return <LiveCustomerDetail />;
  return <DemoCustomerDetail />;
}
function DemoCustomerDetail() {
  const { activeTab, selectTab } = useCustomerTab();
  const { id } = useParams();
  const { customers, entries, setEntries, actions } = useDemo();
  const c = customers.find((c) => c.id === id);
  const [kind, setKind] = useState<"note" | "contact" | "order" | null>(null);
  const [text, setText] = useState("");
  if (!c)
    return (
      <Alert variant="warning">
        Customer not found. <Link to="/customers">Back to customers</Link>
      </Alert>
    );
  return (
    <PreviewGate>
      <Heading
        title={c.name}
        subtitle={`${c.account} · ${c.category} · ${c.city}`}
      >
        <Link className="btn btn-outline-primary" to={`/customers/${id}/edit`}>
          Edit customer
        </Link>
      </Heading>
      <Row className="g-4">
        <Col lg={4}>
          <Card>
            <Card.Header>Account details</Card.Header>
            <Card.Body>
              <Badge bg={c.onStop ? "warning" : "success"} className="mb-3">
                {c.onStop ? "On stop" : "Active"}
              </Badge>
              <p>
                {c.address}
                <br />
                {c.city}
                <br />
                {c.postcode}
              </p>
              <p>
                {c.phone}
                <br />
                <a href={`mailto:${c.email}`}>{c.email}</a>
              </p>
              <hr />
              {[
                ["Owner", c.owner],
                ["Type", c.type],
                ["Grid reference", c.grid],
                ["Discount", c.discount],
                ["Call day", c.callDay],
                ["Delivery day", c.deliveryDay],
              ].map(([k, v]) => (
                <div className="detail-row" key={k}>
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))}
              <hr />
              <p>{c.notes}</p>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8}>
          <Card>
            <Card.Body>
              <Tab.Container activeKey={activeTab} onSelect={selectTab}>
                <Nav variant="tabs" className="mb-4">
                  <Nav.Item><Nav.Link eventKey="contact">Contacts</Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="actions">Actions</Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="note">Notes</Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="order">Submissions</Nav.Link></Nav.Item>
                </Nav>
                <Tab.Content>
                <Tab.Pane eventKey="actions">
                  <Link
                    className="btn btn-primary mb-3"
                    to={`/actions/new/${id}`}
                  >
                    + Add action
                  </Link>
                  {actions
                    .filter((a) => a.customerId === id)
                    .sort((a, b) => b.due.localeCompare(a.due))
                    .map((a) => (
                      <div className="activity" key={a.id}>
                        <Badge bg={a.closed ? "secondary" : "primary"}>
                          {a.closed ? "Completed" : a.type}
                        </Badge>
                        <p className="mt-2 mb-1">{a.text}</p>
                        <small>
                          {a.due} · {a.owner}
                        </small>
                      </div>
                    ))}
                </Tab.Pane>
                {(["contact", "note", "order"] as const).map((k) => (
                  <Tab.Pane eventKey={k} key={k}>
                    <Button
                      className="mb-3"
                      onClick={() => {
                        setKind(k);
                        setText("");
                      }}
                    >
                      + {k === "order" ? "New submission" : `Add ${k}`}
                    </Button>
                    {entries
                      .filter((e) => e.customerId === id && e.kind === k)
                      .sort((a, b) => b.date.localeCompare(a.date))
                      .map((e) => (
                        <div className="activity" key={e.id}>
                          <p>{e.text}</p>
                          <small>{e.date}</small>
                        </div>
                      ))}
                    {!entries.some(
                      (e) => e.customerId === id && e.kind === k,
                    ) && (
                      <p className="text-body-secondary">
                        No {k}s recorded in this demo yet.
                      </p>
                    )}
                  </Tab.Pane>
                ))}
                </Tab.Content>
              </Tab.Container>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal show={!!kind} onHide={() => setKind(null)}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            setEntries([
              ...entries,
              {
                id: crypto.randomUUID(),
                customerId: c.id,
                kind: kind!,
                text,
                date: new Date().toLocaleDateString("en-GB"),
              },
            ]);
            setKind(null);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {kind === "order" ? "New submission" : `Add ${kind}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {kind === "order" && (
              <Alert variant="info">
                Demo submission. Form integration will be connected when the
                API is available.
              </Alert>
            )}
            <Field
              label={
                kind === "contact"
                  ? "Contact name, email and telephone"
                  : kind === "order"
                    ? "Submission details"
                    : "Note"
              }
              type="textarea"
              value={text}
              onChange={setText}
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setKind(null)}>
              Cancel
            </Button>
            <Button type="submit">Save {kind}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PreviewGate>
  );
}
import { ActionPresenceIcon } from "@/components/actions/ActionPresenceIcon";
export function ActionsScreen({ team = false }: { team?: boolean }) {
  const { isDemo } = useAuth();
  const { actions, customers } = useDemo();
  if (!isDemo) return <LiveActionsScreen team={team} />;
  const [closed, setClosed] = useState(false);
  const [q, setQ] = useState("");
  const [dueFilter, setDueFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All types");
  const [categoryFilter, setCategoryFilter] = useState("All categories");
  const [selected, setSelected] = useState<Action | null>(null);
  const rows = actions.filter(
    (a) =>
      a.closed === closed &&
      (dueFilter === "All" ||
        (dueFilter === "Overdue"
          ? a.due.slice(0, 10) < "2026-10-08"
          : a.due.slice(0, 10) === "2026-10-08")) &&
      (typeFilter === "All types" || a.type === typeFilter) &&
      (categoryFilter === "All categories" ||
        customers.find((c) => c.id === a.customerId)?.category ===
          categoryFilter) &&
      (team || a.owner === "Alex Morgan") &&
      `${a.text} ${customers.find((c) => c.id === a.customerId)?.name}`
        .toLowerCase()
        .includes(q.toLowerCase()),
  );
  return (
    <PreviewGate>
      <Heading
        title={team ? "All actions" : "My actions"}
        subtitle="Calls, visits and follow-ups, all in one place."
      >
        <Link className="btn btn-primary" to="/actions/new">
          + Add action
        </Link>
      </Heading>
      <div className="d-flex gap-2 mb-3">
        <Link
          className={`btn btn-${team ? "outline-" : ""}primary`}
          to="/actions/mine"
        >
          My actions
        </Link>
        <Link
          className={`btn btn-${team ? "" : "outline-"}primary`}
          to="/actions/team"
        >
          All actions
        </Link>
      </div>
      <Card>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Field label="Search actions" value={q} onChange={setQ} />
            </Col>
            <Col md={3}>
              <Field
                label="Action types"
                value={typeFilter}
                onChange={setTypeFilter}
                options={["All types", ...new Set(actions.map((a) => a.type))]}
              />
            </Col>
            <Col md={3}>
              <Field
                label="Category"
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={[
                  "All categories",
                  ...new Set(customers.map((c) => c.category)),
                ]}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                id="crm-show-completed"
                className="mt-4"
                label="Show completed"
                checked={closed}
                onChange={(e) => setClosed(e.target.checked)}
              />
            </Col>
          </Row>
          <div className="d-flex flex-wrap gap-2 mt-3">
            {["All", "Overdue", "Today"].map((filter) => (
              <Button
                key={filter}
                size="sm"
                variant={dueFilter === filter ? "primary" : "outline-primary"}
                onClick={() => setDueFilter(filter)}
              >
                Show {filter.toUpperCase()}
              </Button>
            ))}
          </div>
        </Card.Body>
        <div className="table-responsive border rounded-3">
        <Table hover striped className="mb-0">
          <thead>
            <tr>
              <th>Status</th>
              <th>Action</th>
              <th>Category</th>
              {team && (
                <>
                  <th>Assigned to</th>
                  <th>Account owner</th>
                </>
              )}
              <th>Acc.#</th>
              <th>Customer</th>
              <th>Postcode</th>
              <th>To Do</th>
              <th>Next Action On</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td>
                  <Button
                    size="sm"
                    variant={
                      actionStatus(a) === "Overdue"
                        ? "outline-danger"
                        : "outline-primary"
                    }
                    onClick={() => setSelected(a)}
                  >
                    {actionStatus(a)}
                  </Button>
                  {!a.closed && <ActionPresenceIcon actionId={a.id} />}
                </td>
                <td>{a.type}</td>
                <td>
                  {customers.find((c) => c.id === a.customerId)?.category}
                </td>
                {team && (
                  <>
                    <td>{a.owner}</td>
                    <td>
                      {customers.find((c) => c.id === a.customerId)?.owner}
                    </td>
                  </>
                )}
                <td>
                  <Link to={`/customers/${a.customerId}`}>
                    {customers.find((c) => c.id === a.customerId)?.account}
                  </Link>
                </td>
                <td>
                  <Link to={`/customers/${a.customerId}`}>
                    {customers.find((c) => c.id === a.customerId)?.name}
                  </Link>
                </td>
                <td>
                  {customers.find((c) => c.id === a.customerId)?.postcode}
                </td>
                <td>{a.text}</td>
                <td>{a.due.replace("T", " · ")}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        </div>
        {!rows.length && <p className="p-4">No actions match these filters.</p>}
      </Card>
      {selected && (
        <ActionWorkspace
          actionId={selected.id}
          onClose={() => setSelected(null)}
        />
      )}
    </PreviewGate>
  );
}
export function AddActionScreen() {
  const { isDemo } = useAuth();
  const { customerId } = useParams();
  const { customers, actions, setActions } = useDemo();
  if (!isDemo) return <LiveAddActionScreen />;
  const [c, setC] = useState(customerId ?? customers[0]?.id ?? "");
  const [type, setType] = useState("Telephone");
  const [owner, setOwner] = useState("Alex Morgan");
  const [due, setDue] = useState("2026-10-09");
  const [text, setText] = useState("");
  const navigate = useNavigate();
  return (
    <PreviewGate>
      <Heading
        title="Add customer action"
        subtitle="Choose a customer and plan the next conversation."
      />
      <Card className="form-panel">
        <Card.Body>
          <Form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setActions([
                ...actions,
                {
                  id: crypto.randomUUID(),
                  customerId: c,
                  type,
                  owner,
                  due,
                  text,
                  closed: false,
                },
              ]);
              navigate("/actions/team");
            }}
          >
            <Form.Group className="mb-3" controlId="action-customer">
              <Form.Label>Customer</Form.Label>
              <Form.Select
                required
                value={c}
                onChange={(e) => setC(e.target.value)}
              >
                <option value="">Select customer</option>
                {customers.map((x) => (
                  <option value={x.id} key={x.id}>
                    {x.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Field
              label="Due date"
              type="date"
              value={due}
              onChange={setDue}
              required
            />
            <Field
              label="Action type"
              options={["Telephone", "Email", "Visit"]}
              value={type}
              onChange={setType}
            />
            <Field
              label="Assign to"
              options={["Alex Morgan", "Jamie Taylor"]}
              value={owner}
              onChange={setOwner}
            />
            <Field
              label="Action text"
              type="textarea"
              value={text}
              onChange={setText}
              required
            />
            <Button type="submit">Save action</Button>{" "}
            <Link className="btn btn-outline-secondary" to="/actions/mine">
              Cancel
            </Link>
          </Form>
        </Card.Body>
      </Card>
    </PreviewGate>
  );
}
